/**
 * Modal "Nová idea" (F4AI-19, S13) — kind 1021 s `title` tagom, `t`
 * tagmi topicov a voliteľným `t` tagom `agent` (checkbox "publikujem
 * ako agent" — F1 heuristika author-kind filtru, viď `lib/ideas.ts`).
 *
 * Signing je za rovnakým rozhraním `Signer` ako Composer (NIP-07 má
 * prednosť, inak nsec z identity). Publish → `POST /events`; po úspechu
 * callback `onPublished` (stránka invaliduje + refetchne board) a modal
 * sa zavrie. Chyba → inline error s `reasons` z `ApiError`.
 *
 * Dopyt na validáciu: title je povinný (gateway by síce vytvoril
 * "(bez názvu)", ale board bez názvu je na hovno), text môže byť
 * prázdny (description je voliteľné pole row).
 */
import { useState, type FormEvent, type JSX } from 'react';

import { useApi } from '../api/ApiContext.js';
import { ApiError } from '../api/client.js';
import { useIdentity } from '../nostr/IdentityContext.js';
import { useWriteStatus } from '../hooks/useWriteStatus.js';
import { resolveSigner } from '../nostr/signer.js';
import { buildNewIdeaTemplate } from '../lib/ideas.js';

export function NewIdeaModal({
  onClose,
  onPublished,
}: {
  onClose: () => void;
  onPublished: () => void;
}): JSX.Element {
  const api = useApi();
  const { identity } = useIdentity();
  // B10: write-gate — modal je zamknutý pre neregistrovaných (403 by
  // prišiel od servera tak či tak, len zbytočne neposielame event)
  const write = useWriteStatus();
  const locked = write.locked;
  const lockTitle = locked ? `písanie zamknuté: ${write.reason ?? 'write status unavailable'}` : undefined;
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [topicsRaw, setTopicsRaw] = useState('');
  const [asAgent, setAsAgent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(form: FormEvent<HTMLFormElement>): Promise<void> {
    form.preventDefault();
    setError(null);
    if (identity === null) {
      setError('identita nie je dostupná — zahoď kľúč a skús ho vygenerovať znova');
      return;
    }
    if (title.trim() === '') {
      setError('myšlienka (1021) vyžaduje názov');
      return;
    }
    if (locked) {
      setError(`písanie zamietnuté: ${write.reason ?? 'write status unavailable'}`);
      return;
    }
    setBusy(true);
    try {
      const template = buildNewIdeaTemplate({
        title,
        text,
        topics: topicsRaw.split(/[,\s]+/),
        asAgent,
        createdAt: Math.floor(Date.now() / 1000),
      });
      const signer = await resolveSigner({ secretKey: identity.secretKey });
      const event = await signer.sign(template);
      const result = await api.publish(event);
      if (result.quorum_met) {
        onPublished();
        onClose();
      } else {
        setError(result.error ?? 'kvórum relayov nesplnené');
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.reasons.join('; ') : err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" data-testid="new-idea-modal" role="dialog" aria-modal="true">
      <form className="glass-card glass-modal" onSubmit={(e) => void onSubmit(e)}>
        <div className="modal-head">
          <p className="side-title">nová idea (kind 1021)</p>
          <button
            type="button"
            className="modal-close"
            aria-label="zavrieť"
            data-testid="new-idea-close"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className="field">
          <label htmlFor="idea-title">názov</label>
          <input
            id="idea-title"
            name="idea-title"
            value={title}
            disabled={locked}
            title={lockTitle}
            onChange={(e) => {
              setTitle(e.target.value);
            }}
            placeholder="napr. Agent na audit Nostr pravidiel"
            data-testid="new-idea-title"
          />
        </div>

        <div className="field">
          <label htmlFor="idea-text">text (popis)</label>
          <textarea
            id="idea-text"
            name="idea-text"
            value={text}
            disabled={locked}
            title={lockTitle}
            onChange={(e) => {
              setText(e.target.value);
            }}
            placeholder="čo myšlienka je, rozsah, odmena…"
            data-testid="new-idea-text"
          />
        </div>

        <div className="field">
          <label htmlFor="idea-topics">topics (čiarkou alebo medzerou)</label>
          <input
            id="idea-topics"
            name="idea-topics"
            value={topicsRaw}
            disabled={locked}
            title={lockTitle}
            onChange={(e) => {
              setTopicsRaw(e.target.value);
            }}
            placeholder="napr. rust, nostr-agents"
            data-testid="new-idea-topics"
          />
        </div>

        <label className="agent-check-row" htmlFor="idea-agent">
          <input
            id="idea-agent"
            type="checkbox"
            checked={asAgent}
            disabled={locked}
            title={lockTitle}
            onChange={(e) => {
              setAsAgent(e.target.checked);
            }}
            data-testid="new-idea-agent-check"
          />
          publikujem ako agent (pridá `t` tag `agent`)
        </label>

        <div className="composer-actions">
          <button
            type="submit"
            className="primary-btn"
            disabled={busy || locked}
            title={lockTitle}
            data-testid="new-idea-submit"
          >
            {busy ? 'publikujem…' : 'publikovať myšlienku'}
          </button>
          <button
            type="button"
            className="ghost-btn"
            data-testid="new-idea-cancel"
            onClick={onClose}
          >
            zrušiť
          </button>
        </div>

        {locked && (
          <p className="notice" data-testid="new-idea-locked" title={lockTitle}>
            {write.reason ?? 'write status unavailable'} — písanie je len pre registrovaných operátorov
          </p>
        )}

        {error !== null && (
          <p className="notice error" role="alert" data-testid="new-idea-error">
            {error}
          </p>
        )}
      </form>
    </div>
  );
}
